/**
 * QL-212 — the single, pure source of truth for risk-assessment input scope.
 *
 * A risk model says who each of its three inputs belongs to: the goal
 * ("goal" — today's behaviour, captured once per goal) or the client
 * ("global" — captured once per flow instance and fanned out to every goal).
 * The three inputs are the time horizon, the risk level (risk tolerance) and
 * the adjustment questions, which are scoped as one whole set and never per
 * question (D2).
 *
 * Every scope rule in the feature belongs here, so the readers, the write
 * paths, the flow editor, the PDF builder and the completion engine can never
 * disagree. No I/O, no database, no framework: a caller that needs a lookup
 * passes it in as a callback (the habit `conditional-sources.ts` established).
 *
 * This module owns scope normalisation, the change diff the write lock uses
 * (D14), the rendered-section set for each container — the flow page and the
 * PDF section, which follow the same rule — the merge of global
 * and goal inputs into a goal's *effective* inputs, the completeness verdict,
 * and the write-scope violations every write path refuses on. Extend this file
 * rather than starting a second one.
 */

import type { FlowPageType } from "./flow-component-policy";
import { unansweredAdjustmentQuestions } from "./risk-adjustment-scoring";
import type { RiskAssessmentInput } from "./types/flow-instances";
import type { PdfSectionScope } from "./types/pdf-templates";
import {
  RISK_INPUT_SCOPES,
  type RiskAdjustmentQuestion,
  type RiskCompletenessVerdict,
  type RiskInputScope,
  type RiskModelScopes,
  type RiskSection,
} from "./types/risk-models";

/** Whether a value is one of the two scopes exactly. */
export function isRiskInputScope(value: unknown): value is RiskInputScope {
  return (RISK_INPUT_SCOPES as readonly unknown[]).includes(value);
}

/**
 * Read one stored scope. Anything that is not exactly "goal" or "global" —
 * absent, null, or a value written by some future or stale configuration —
 * reads as "goal", the per-goal behaviour every model had before QL-212.
 */
export function normalizeRiskInputScope(value: unknown): RiskInputScope {
  return isRiskInputScope(value) ? value : "goal";
}

/**
 * The three scopes of a risk model, normalised. Takes a partial row so a
 * caller holding a legacy or trimmed model — one selected before the columns
 * existed — still gets a complete record back.
 */
export function resolveRiskInputScopes(
  model: Partial<RiskModelScopes> | null | undefined,
): RiskModelScopes {
  return {
    timeHorizonScope: normalizeRiskInputScope(model?.timeHorizonScope),
    riskLevelScope: normalizeRiskInputScope(model?.riskLevelScope),
    adjustmentQuestionsScope: normalizeRiskInputScope(model?.adjustmentQuestionsScope),
  };
}

/** The three scope keys, in the order the editor shows them. */
export const RISK_INPUT_SCOPE_KEYS = [
  "timeHorizonScope",
  "riskLevelScope",
  "adjustmentQuestionsScope",
] as const satisfies readonly (keyof RiskModelScopes)[];

/**
 * Which of the three scopes a request would actually change (D14).
 *
 * A key counts only when the request carries it AND its value differs from the
 * stored one. Both sides go through `normalizeRiskInputScope`, which is what
 * makes a legacy row work: a model written before the columns existed reads as
 * all-"goal", so a request setting the explicit default diffs to nothing rather
 * than being treated as a change the lock would then refuse.
 *
 * The caller compares, rather than refusing on the mere presence of a scope
 * key, because this is a read-modify-write payload: the settings screen resends
 * the model's name and description on every unrelated save, and a full-object
 * PATCH that leaves the scopes alone must not be mistaken for a scope change.
 */
export function changedRiskInputScopes(
  current: Partial<RiskModelScopes> | null | undefined,
  requested: Partial<RiskModelScopes> | null | undefined,
): (keyof RiskModelScopes)[] {
  if (!requested) return [];
  const stored = resolveRiskInputScopes(current);

  return RISK_INPUT_SCOPE_KEYS.filter(
    (key) =>
      requested[key] !== undefined && normalizeRiskInputScope(requested[key]) !== stored[key],
  );
}

// ─── Rendered sections (D5) ──────────────────────────────────────────────────

/**
 * Which of the widget's four sections render on a flow page of `pageType`, for
 * a model with these scopes.
 *
 * The three input sections follow their own scope directly: a global page shows
 * what is captured once for the client, a goal page shows what is captured per
 * goal. A portfolio page shows nothing — the risk assessment has no
 * per-portfolio form.
 *
 * The result block (Initial · Adjustment · Final · Level) is not an input, so it
 * follows the SCORE instead. A score is per goal always (D7), so the block
 * belongs on the goal page whenever any input is goal-scoped — that is the page
 * where the number is this page's own conclusion and where two goals visibly
 * differ. Only when all three inputs are global does the goal page contribute
 * nothing to the score, and the block moves to the global page.
 *
 * An EMPTY set is a meaningful answer, not an error: it says this placement
 * would render nothing. Callers use it to refuse a placement at save time and
 * to drop a legacy placement from flow completion.
 */
export function renderedRiskSections(
  scopes: RiskModelScopes,
  pageType: FlowPageType,
): ReadonlySet<RiskSection> {
  if (pageType === "portfolio") return new Set<RiskSection>();

  const wanted: RiskInputScope = pageType === "global" ? "global" : "goal";
  const sections = new Set<RiskSection>();
  if (scopes.timeHorizonScope === wanted) sections.add("timeHorizon");
  if (scopes.riskLevelScope === wanted) sections.add("riskLevel");
  if (scopes.adjustmentQuestionsScope === wanted) sections.add("adjustmentQuestions");

  const allGlobal =
    scopes.timeHorizonScope === "global" &&
    scopes.riskLevelScope === "global" &&
    scopes.adjustmentQuestionsScope === "global";
  if (pageType === "global" ? allGlobal : !allGlobal) sections.add("result");

  return sections;
}

/** Convenience for the two guards that only care whether anything shows at all. */
export function riskPlacementRendersNothing(
  scopes: RiskModelScopes,
  pageType: FlowPageType,
): boolean {
  return renderedRiskSections(scopes, pageType).size === 0;
}

// ─── Rendered sections in a PDF section (D11) ────────────────────────────────

/** A PDF section scope is the same container shape as a flow page type. */
const PDF_SECTION_PAGE_TYPE = {
  global: "global",
  per_goal: "goal",
  per_portfolio: "portfolio",
} as const satisfies Record<PdfSectionScope, FlowPageType>;

/**
 * Which sections the risk block prints in a PDF section of `sectionScope`.
 *
 * The PDF follows the flow rule exactly: each input prints in the container
 * its scope designates, and the result prints where the flow's result block
 * lands. So a client-level answer prints ONCE, in a global section, instead of
 * being repeated under every goal — which is what the feature exists to remove.
 *
 * - **global** prints the inputs captured per client, plus the result when all
 *   three are.
 * - **per_goal** prints the inputs captured per goal, plus the result unless all
 *   three are global.
 * - **per_portfolio** never prints: the assessment has no per-portfolio form.
 *
 * An empty set is a meaningful answer, as on the flow side: it says this
 * placement would print nothing, and the PDF-editor save guard refuses on it.
 * That happens in exactly two configurations — a global section under an
 * all-per-goal model, and a per-goal section under an all-global one.
 */
export function renderedRiskSectionsInPdfSection(
  scopes: RiskModelScopes,
  sectionScope: PdfSectionScope,
): ReadonlySet<RiskSection> {
  return renderedRiskSections(scopes, PDF_SECTION_PAGE_TYPE[sectionScope]);
}

/** The twin of `riskPlacementRendersNothing`, for the PDF-editor save guard. */
export function riskPdfPlacementRendersNothing(
  scopes: RiskModelScopes,
  sectionScope: PdfSectionScope,
): boolean {
  return renderedRiskSectionsInPdfSection(scopes, sectionScope).size === 0;
}

// ─── Merge on read (D4) ──────────────────────────────────────────────────────

/**
 * A goal's EFFECTIVE risk inputs: each field taken from the row its scope
 * designates — the instance-level row for a global input, the goal's own row
 * for a goal-scoped one.
 *
 * This is the function every reader calls, and the reason the rule cannot
 * drift. Note what it deliberately does NOT do: it never falls back from one
 * row to the other. A missing goal value under a global scope means "read the
 * instance row", and a missing instance value under a goal scope means
 * "unanswered" — inferring scope from which row happens to hold data would make
 * a half-migrated instance interpret itself differently on every read.
 */
export function effectiveRiskInputs(
  globalInput: RiskAssessmentInput | null | undefined,
  goalInput: RiskAssessmentInput | null | undefined,
  scopes: RiskModelScopes,
): RiskAssessmentInput {
  const from = (scope: RiskInputScope) => (scope === "global" ? globalInput : goalInput);

  return {
    timeHorizonId: from(scopes.timeHorizonScope)?.timeHorizonId,
    riskLevelId: from(scopes.riskLevelScope)?.riskLevelId,
    adjustmentAnswers: from(scopes.adjustmentQuestionsScope)?.adjustmentAnswers ?? {},
  };
}

// ─── Completeness (D3, D10) ──────────────────────────────────────────────────

/**
 * Whether a set of effective inputs can be scored, and if not, why.
 *
 * These reasons are all "the client has not finished answering". A pair that IS
 * answered but has no cell in the model's matrix is deliberately not represented
 * here: that is a configuration failure, reported separately by the apply step,
 * and this pure module has no matrix to consult.
 *
 * The questions check delegates to `unansweredAdjustmentQuestions`, so
 * "answered" means exactly what it means everywhere else: every ENABLED
 * question carries an answer id that is one of that question's own options.
 */
export function riskInputsCompleteness(
  inputs: RiskAssessmentInput,
  adjustmentQuestions: RiskAdjustmentQuestion[] | null | undefined,
): RiskCompletenessVerdict {
  if (!inputs.timeHorizonId) return { scoreable: false, reason: "no_time_horizon" };
  if (!inputs.riskLevelId) return { scoreable: false, reason: "no_risk_level" };

  const unanswered = unansweredAdjustmentQuestions(adjustmentQuestions, inputs.adjustmentAnswers);
  if (unanswered.length > 0) {
    return {
      scoreable: false,
      reason: "unanswered_adjustment_questions",
      unansweredQuestionIds: unanswered.map((q) => q.id),
    };
  }

  return { scoreable: true };
}

// ─── Section completeness (D3, D12) ──────────────────────────────────────────

/**
 * Is every INPUT section this container renders actually answered?
 *
 * "This container" is a flow page (or, from 011, a PDF section): the caller
 * passes the set `renderedRiskSections` gave it, and the inputs that belong to
 * the row that container writes to — the instance-level row for a global page,
 * the goal's own row for a goal page.
 *
 * The `result` section is deliberately ignored: it is a readout of the score,
 * not something a client can answer, so it can never be "unanswered". An empty
 * set is trivially answered — a placement that renders nothing has nothing to
 * ask, and callers that care about that case use `riskPlacementRendersNothing`.
 *
 * One rule, three callers: the completion engine's global slot, the widget's
 * decision to submit rather than partial-save, and the public flow's mirror of
 * it. Keeping it here is what stops the three drifting apart.
 */
export function riskSectionsAnswered(
  sections: ReadonlySet<RiskSection>,
  inputs: RiskAssessmentInput | null | undefined,
  adjustmentQuestions: RiskAdjustmentQuestion[] | null | undefined,
): boolean {
  if (sections.has("timeHorizon") && !inputs?.timeHorizonId) return false;
  if (sections.has("riskLevel") && !inputs?.riskLevelId) return false;
  if (
    sections.has("adjustmentQuestions") &&
    unansweredAdjustmentQuestions(adjustmentQuestions, inputs?.adjustmentAnswers).length > 0
  ) {
    return false;
  }
  return true;
}

// ─── Write-scope violations (D3) ─────────────────────────────────────────────

/**
 * Which row a write targets: one goal's row, or the instance-level (client) row.
 * The vocabulary is the caller's, not the page's — a write has no page type.
 */
export type RiskWriteTarget = "goal" | "instance";

/**
 * Whether a write target owns no input at all under these scopes — its shape
 * has nothing to take, so every write to it is refused.
 *
 * That is exactly its page rendering nothing: the result block only ever joins
 * a page that already owns an input. So "instance owns nothing" means every
 * input is per goal, and "goal owns nothing" means every input is per client.
 */
export function riskWriteTargetOwnsNothing(
  scopes: RiskModelScopes,
  target: RiskWriteTarget,
): boolean {
  return riskPlacementRendersNothing(scopes, target === "instance" ? "global" : "goal");
}

/** The input fields a write can carry, named as they appear on the wire. */
export type RiskWriteField = "timeHorizonId" | "riskLevelId" | "adjustmentAnswers";

/** The section each writable field belongs to, in RISK_SECTIONS order. */
const WRITE_FIELD_SECTIONS = [
  ["timeHorizonId", "timeHorizon"],
  ["riskLevelId", "riskLevel"],
  ["adjustmentAnswers", "adjustmentQuestions"],
] as const satisfies readonly (readonly [RiskWriteField, RiskSection])[];

/**
 * Which fields of this write belong to the OTHER row (D3). An empty array
 * means the write is well scoped.
 *
 * The rule is exactly the rendered-section rule: the row a write targets owns
 * the sections its page renders, so a field is a violation when its section is
 * not in that set. A field the caller did not send is never a violation,
 * whatever the scopes — omitting an input is always legal.
 *
 * `present` says which fields the write actually carries, not their values, so
 * a caller can decide for itself whether an empty string counts as sent.
 * Results come back in RISK_SECTIONS order, so a message built from several
 * violations reads the same way every time.
 */
export function riskWriteScopeViolations(
  scopes: RiskModelScopes,
  target: RiskWriteTarget,
  present: Partial<Record<RiskWriteField, boolean>>,
): RiskWriteField[] {
  const owned = renderedRiskSections(scopes, target === "instance" ? "global" : "goal");
  return WRITE_FIELD_SECTIONS.filter(
    ([field, section]) => present[field] && !owned.has(section),
  ).map(([field]) => field);
}

/**
 * Each writable field's scope, keyed by the field name a caller actually sends
 * (QL-212).
 *
 * The same three scopes the model stores, said in the vocabulary of a request:
 * an API caller asks "may I send `riskLevelId` on this shape?", not "what is
 * `riskLevelScope`?". Derived from the one field→section table above, so a read
 * endpoint reporting scopes and a write path refusing on them can never disagree.
 */
export function riskWriteFieldScopes(
  scopes: RiskModelScopes,
): Record<RiskWriteField, RiskInputScope> {
  const global = renderedRiskSections(scopes, "global");
  return Object.fromEntries(
    WRITE_FIELD_SECTIONS.map(([field, section]) => [
      field,
      global.has(section) ? "global" : "goal",
    ]),
  ) as Record<RiskWriteField, RiskInputScope>;
}

/** How each field is named in a refusal. */
const WRITE_FIELD_LABELS: Record<RiskWriteField, string> = {
  timeHorizonId: "the time horizon",
  riskLevelId: "the risk level",
  adjustmentAnswers: "the adjustment questions",
};

/**
 * The refusal sentence for one violation — the same words on every surface
 * (D3), which is the point of routing every write path through here.
 *
 * A write aimed at the instance row is told the input is per goal; a write
 * aimed at a goal is told it is per client AND where to send it instead, since
 * "submit it once for the flow instance" is the shape the caller has to find.
 */
export function riskWriteScopeMessage(field: RiskWriteField, target: RiskWriteTarget): string {
  const label = WRITE_FIELD_LABELS[field];
  if (target === "instance") {
    return `This flow's risk model captures ${label} per goal, not per client`;
  }
  const verb = field === "adjustmentAnswers" ? "submit them" : "submit it";
  return `This flow's risk model captures ${label} per client, not per goal — ${verb} once for the flow instance instead`;
}
